import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AppLayout } from "@/components/intel/Layout";
import { ModuleShell, StatTile } from "@/components/intel/ModuleShell";
import { listScanRuns, deleteScanRun } from "@/lib/intel/feeds.functions";
import { Archive, Trash2, RefreshCw, ShieldCheck } from "lucide-react";

export const Route = createFileRoute("/vault/")({
  head: () => ({ meta: [
    { title: "Scan Vault · CARIO INTEL" },
    { name: "description", content: "Encrypted per-user vault of every intelligence scan you have launched." },
  ] }),
  component: VaultPage,
});

function VaultPage() {
  return (
    <AppLayout>
      <VaultInner />
    </AppLayout>
  );
}

function VaultInner() {
  const list = useServerFn(listScanRuns);
  const del = useServerFn(deleteScanRun);
  const qc = useQueryClient();

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["scan-vault"],
    queryFn: () => list(),
    staleTime: 30_000,
  });
  const runs = data ?? [];

  const removeM = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["scan-vault"] });
    },
  });

  const total = runs.length;
  const completed = runs.filter((r) => r.status === "completed").length;
  const failed = runs.filter((r) => r.status === "failed").length;
  const avgRisk = total ? Math.round(runs.reduce((s, r) => s + r.riskScore, 0) / total) : 0;

  return (
    <ModuleShell
      eyebrow="Scan Vault"
      title="Your private intelligence archive"
      description="Every scan you launch is captured here under row-level security. Only you (and admins) can read or remove these records."
      meta={
        <button onClick={() => refetch()} className="inline-flex items-center gap-2 rounded-md border border-panel-border bg-panel/40 px-3 py-1.5 text-xs">
          <RefreshCw className={`h-3 w-3 ${isFetching ? "animate-spin" : ""}`} /> Refresh
        </button>
      }
    >
      <div className="grid gap-3 md:grid-cols-4">
        <StatTile label="Total scans" value={total} />
        <StatTile label="Completed" value={completed} tone="success" />
        <StatTile label="Failed" value={failed} tone="danger" />
        <StatTile label="Avg risk" value={avgRisk} tone="amber" />
      </div>

      <div className="glass rounded-xl p-2">
        {isLoading && <div className="p-6 text-center text-sm text-muted-foreground">Loading vault…</div>}
        {!isLoading && runs.length === 0 && (
          <div className="p-8 text-center">
            <Archive className="mx-auto h-8 w-8 text-muted-foreground" />
            <div className="mt-3 text-sm text-muted-foreground">No scans archived yet. Run an investigation to populate your vault.</div>
            <Link to="/investigate" className="mt-4 inline-flex items-center gap-2 rounded-md border border-cyan/40 bg-cyan/10 px-3 py-1.5 text-xs text-cyan">Start a scan</Link>
          </div>
        )}
        {runs.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                <tr className="border-b border-panel-border">
                  <th className="px-3 py-2">Target</th>
                  <th className="px-3 py-2">Type</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Risk</th>
                  <th className="px-3 py-2">Conf</th>
                  <th className="px-3 py-2">Sources</th>
                  <th className="px-3 py-2">Created</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id} className="border-b border-panel-border/60 hover:bg-panel/40">
                    <td className="px-3 py-2 truncate max-w-[260px]">{r.target}</td>
                    <td className="px-3 py-2 font-mono text-[11px] uppercase text-muted-foreground">{r.targetType}</td>
                    <td className="px-3 py-2">
                      <span className={`rounded px-1.5 py-0.5 font-mono text-[10px] uppercase ${r.status === "completed" ? "bg-success/15 text-success" : r.status === "failed" ? "bg-danger/15 text-danger" : "bg-amber/15 text-amber"}`}>{r.status}</span>
                    </td>
                    <td className="px-3 py-2 font-mono">{r.riskScore}</td>
                    <td className="px-3 py-2 font-mono">{r.confidence}%</td>
                    <td className="px-3 py-2 font-mono">{r.sourceCount}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground">{new Date(r.createdAt).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">
                      <Link
                        to="/vault/$scanId"
                        params={{ scanId: r.id }}
                        className="mr-2 text-xs text-cyan hover:underline"
                      >
                        Open
                      </Link>
                      <button
                        onClick={() => { if (confirm(`Permanently delete scan of "${r.target}"?`)) removeM.mutate(r.id); }}
                        className="text-muted-foreground hover:text-danger"
                        title="Delete from vault"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="glass rounded-xl p-3 text-xs text-muted-foreground flex items-center gap-2">
        <ShieldCheck className="h-3.5 w-3.5 text-success" />
        Scans are stored in your Lovable Cloud database under row-level security. Other operators cannot read your vault.
      </div>
    </ModuleShell>
  );
}